/*
 * Native-resolution repaint for the two contain-fit artwork images in
 * Ehlyianha Faith Gil's A4 cut PDF.
 *
 * html2canvas correctly preserves the page layout, but it can soften images
 * that are represented as CSS backgrounds in the export clone. This finalizer
 * runs immediately before the final A4 canvas is encoded and repaints only the
 * dress-code and venue images from their original loaded DOM image elements.
 *
 * It does not change layout, dimensions, object-fit, object-position, or any
 * other invitation element.
 */

const YHANA_EXPORT_WRAPPER_SELECTOR =
  '.pl-wrapper.pl-size-a4.pl-layout-cut:is(.pl-pdf-exporting, .pl-capturing)';
const DRESS_IMAGE_SELECTOR = '.yhp-page--dress-reminders img.yhp-janine-dress-image';
const VENUE_IMAGE_SELECTOR = '.yhp-page--venue-janine .yhp-venue-janine-photo > img';
const VENUE_OVERLAY_SELECTOR = '.yhp-page--venue-janine .yhp-venue-janine-photo > div';
const A4_ASPECT_RATIO = 210 / 297;
const A4_RATIO_TOLERANCE = 0.025;

function isA4PortraitCanvas(canvas) {
  if (!(canvas instanceof HTMLCanvasElement)) return false;
  if (!(canvas.width > 0) || !(canvas.height > 0)) return false;

  return Math.abs((canvas.width / canvas.height) - A4_ASPECT_RATIO) <= A4_RATIO_TOLERANCE;
}

function resolveActiveSheet(wrapper) {
  const sheets = Array.from(wrapper.querySelectorAll('.pl-sheet--cut'));
  if (sheets.length === 0) return null;

  const statusText = wrapper.querySelector('[data-yhana-export-status]')?.textContent || '';
  const statusMatch = statusText.match(/sheet\s+(\d+)\s+of\s+(\d+)/i);

  if (statusMatch) {
    const sheetIndex = Number.parseInt(statusMatch[1], 10) - 1;
    if (sheetIndex >= 0 && sheetIndex < sheets.length) return sheets[sheetIndex];
  }

  return sheets[0];
}

function calculateContainedDestination(image, box) {
  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;

  if (!(naturalWidth > 0) || !(naturalHeight > 0)) return null;

  const scale = Math.min(box.width / naturalWidth, box.height / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;

  return {
    x: box.x + ((box.width - width) / 2),
    y: box.y + ((box.height - height) / 2),
    width,
    height,
  };
}

function repaintContainedImage(canvas, sheet, selector, options = {}) {
  const image = sheet.querySelector(selector);
  if (!(image instanceof HTMLImageElement)) return;
  if (!image.complete || !(image.naturalWidth > 0) || !(image.naturalHeight > 0)) return;

  const sheetRect = sheet.getBoundingClientRect();
  const imageRect = image.getBoundingClientRect();

  if (!(sheetRect.width > 0) || !(sheetRect.height > 0)) return;
  if (!(imageRect.width > 0) || !(imageRect.height > 0)) return;

  const scaleX = canvas.width / sheetRect.width;
  const scaleY = canvas.height / sheetRect.height;
  const box = {
    x: (imageRect.left - sheetRect.left) * scaleX,
    y: (imageRect.top - sheetRect.top) * scaleY,
    width: imageRect.width * scaleX,
    height: imageRect.height * scaleY,
  };

  let clipHeight = box.height;
  if (options.overlaySelector) {
    const overlay = sheet.querySelector(options.overlaySelector);
    if (overlay instanceof HTMLElement) {
      const overlayRect = overlay.getBoundingClientRect();
      const visibleImageHeight = (overlayRect.top - imageRect.top) * scaleY;
      if (visibleImageHeight > 0 && visibleImageHeight < clipHeight) {
        clipHeight = visibleImageHeight;
      }
    }
  }

  const destination = calculateContainedDestination(image, box);
  if (!destination) return;

  const context = canvas.getContext('2d');
  if (!context) return;

  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.globalAlpha = 1;
  context.globalCompositeOperation = 'source-over';
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  context.beginPath();
  context.rect(box.x, box.y, box.width, clipHeight);
  context.clip();

  context.fillStyle = options.backgroundColor || '#fffaf4';
  context.fillRect(box.x, box.y, box.width, clipHeight);
  context.drawImage(
    image,
    destination.x,
    destination.y,
    destination.width,
    destination.height,
  );

  context.restore();
}

function repaintYhanaNativeImages(canvas) {
  if (!isA4PortraitCanvas(canvas)) return;

  const wrapper = document.querySelector(YHANA_EXPORT_WRAPPER_SELECTOR);
  if (!(wrapper instanceof HTMLElement)) return;

  const sheet = resolveActiveSheet(wrapper);
  if (!(sheet instanceof HTMLElement)) return;

  repaintContainedImage(canvas, sheet, DRESS_IMAGE_SELECTOR, {
    backgroundColor: '#fffaf4',
  });

  repaintContainedImage(canvas, sheet, VENUE_IMAGE_SELECTOR, {
    backgroundColor: '#fffaf4',
    overlaySelector: VENUE_OVERLAY_SELECTOR,
  });
}

if (!window.__ehlyianhaPdfNativeImageFinalizerInstalled) {
  window.__ehlyianhaPdfNativeImageFinalizerInstalled = true;

  const originalToDataURL = HTMLCanvasElement.prototype.toDataURL;

  HTMLCanvasElement.prototype.toDataURL = function patchedToDataURL(type, quality) {
    const normalizedType = String(type || 'image/png').toLowerCase();

    if (normalizedType === 'image/jpeg' || normalizedType === 'image/jpg') {
      try {
        repaintYhanaNativeImages(this);
      } catch (error) {
        console.warn('Yhana native image finalizer could not repaint artwork:', error);
      }
    }

    return originalToDataURL.call(this, type, quality);
  };
}
